import { useState, useCallback, useMemo } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, IconButton,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AdminListPanel } from '../admin/AdminListPanel';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import { ConfirmDialog } from '../../shared/components/Dialogs';
import type { PatientRecord } from '../../api/types';

const columns: GridColDef[] = [
  { field: 'patientId', headerName: 'Patient ID', width: 110 },
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
  { field: 'sex', headerName: 'Sex', width: 50 },
  { field: 'dob', headerName: 'DOB', width: 100 },
  { field: 'phone', headerName: 'Phone', width: 120 },
  { field: 'siteName', headerName: 'Site', width: 100 },
  {
    field: 'actions',
    headerName: '',
    width: 80,
    sortable: false,
    renderCell: () => null,
  },
];

/**
 * Patient management panel — PatientListPanel equivalent.
 * Lists patients with search, edit, delete.
 */
export const PatientPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editPatient, setEditPatient] = useState<PatientRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PatientRecord | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.patients.all, search],
    queryFn: () => api.getPatients(0, 500, search || undefined),
    staleTime: 30_000,
  });

  const patients = useMemo(() => data?.records ?? [], [data]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
  }, [queryClient]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await api.deletePatient(deleteTarget.objKey);
      pushSuccess('Patient deleted successfully');
      refresh();
    } catch (e) {
      pushError(`Failed to delete patient: ${e instanceof Error ? e.message : String(e)}`);
    }
    setDeleteTarget(null);
  }, [deleteTarget, refresh]);

  return (
    <>
      <AdminListPanel
        title="Patients"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        statusText={`${patients.length} patients`}
      >
        <DataGrid
          rows={patients}
          columns={columns.map((col) =>
            col.field === 'actions'
              ? {
                  ...col,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => setEditPatient(params.row as PatientRecord)} sx={{ width: 24, height: 24 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(params.row as PatientRecord)} sx={{ width: 24, height: 24 }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ),
                }
              : col,
          )}
          getRowId={(row) => (row as PatientRecord).objKey}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {editPatient && (
        <PatientDialog
          patient={editPatient}
          open
          onClose={() => setEditPatient(null)}
          onSave={() => { setEditPatient(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          title="Delete Patient"
          message={`Are you sure you want to delete patient "${deleteTarget.name}" (${deleteTarget.patientId})?`}
          destructive
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
};

/* ── PatientDialog ── */
interface PatientDialogProps {
  patient: PatientRecord;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const PatientDialog = ({ patient, open, onClose, onSave }: PatientDialogProps) => {
  const [firstName, setFirstName] = useState(patient.firstName ?? '');
  const [lastName, setLastName] = useState(patient.lastName ?? '');
  const [dob, setDob] = useState(patient.dob ?? '');
  const [sex, setSex] = useState(patient.sex ?? '');
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [email, setEmail] = useState(patient.email ?? '');
  const [address, setAddress] = useState(patient.address ?? '');
  const [city, setCity] = useState(patient.city ?? '');
  const [state, setState] = useState(patient.state ?? '');
  const [zip, setZip] = useState(patient.zip ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.addModifyPatient({
        objKey: patient.objKey,
        patientId: patient.patientId,
        firstName, lastName, dob, sex, phone, email,
        address, city, state, zip,
        name: `${lastName}, ${firstName}`,
      });
      pushSuccess('Patient saved successfully');
      onSave();
    } catch (e) {
      pushError(`Failed to save patient: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  }, [patient, firstName, lastName, dob, sex, phone, email, address, city, state, zip, onSave]);

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: 500, borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Edit Patient
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <FieldRow label="Patient ID"><TextField size="small" fullWidth value={patient.patientId} disabled sx={fsx} /></FieldRow>
        <FieldRow label="First Name"><TextField size="small" fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="Last Name"><TextField size="small" fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="DOB"><TextField size="small" fullWidth type="date" value={dob} onChange={(e) => setDob(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="Sex"><TextField size="small" fullWidth value={sex} onChange={(e) => setSex(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="Phone"><TextField size="small" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="Email"><TextField size="small" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="Address"><TextField size="small" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="City"><TextField size="small" fullWidth value={city} onChange={(e) => setCity(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="State"><TextField size="small" fullWidth value={state} onChange={(e) => setState(e.target.value)} sx={fsx} /></FieldRow>
        <FieldRow label="ZIP"><TextField size="small" fullWidth value={zip} onChange={(e) => setZip(e.target.value)} sx={fsx} /></FieldRow>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontSize: 12 }}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 80, textAlign: 'right', flexShrink: 0 }}>{label}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
